import React, { useRef, useEffect, useState, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { css, keyframes } from '@emotion/react'

import {
  ButtonAction,
  ACTION_BUTTON_TYPE_ACTIONS
} from '../ButtonAction/ButtonAction'

import { ContextMenu, CONTEXT_MENU_VARIANT_DARK } from '../ContextMenu'

import { useViewport } from '../../hooks'

import { rectBottom } from '../../helpers/viewport'

import colorNames from '../../design/color-names'

export const DataTableContainer = styled.div`
  label: datatable-container;

  border-color: ${colorNames.geyser};
  border-style: solid;

  ${({ $hScroll }) => ($hScroll ? 'border-bottom-width: 1px;' : '')}

  position:relative;

  .ps__rail-x {
    background-color: #eee;
    opacity: 0.9;
  }
`

//

export const DataTableActionMenuTrigger = ({ disabled, rowIndex, onClick }) => {
  const handleClick = e => {
    e.preventDefault()
    e.stopPropagation()
    //
    const rect = e.target.getBoundingClientRect()

    const tableRect = e.target
      .closest('.c-datatable__overlay-container')
      .getBoundingClientRect()

    onClick(rowIndex, {
      top: rect.top - tableRect.top - rect.height / 2,
      bottom: tableRect.bottom - rect.bottom - rect.height / 2,
      left: 0,
      targetRect: { top: rect.top, bottom: rect.bottom }
    })
  }

  return (
    <ButtonAction
      disabled={disabled}
      type={ACTION_BUTTON_TYPE_ACTIONS}
      onClick={handleClick}
      data-mu-test='dt-actionmenu-trigger'
    />
  )
}

DataTableActionMenuTrigger.defaultProps = {
  disabled: false
}

DataTableActionMenuTrigger.propTypes = {
  disabled: PropTypes.bool,
  rowIndex: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired
}

const animDownward = keyframes`
  0% {
    transform: translateY(16px);
  }
  100% {
    transform: translateY(0);
  }`

const animUpward = keyframes`
  0% {
    transform: translateY(-16px);
  }
  100% {
    transform: translateY(0);
  }`

const fadeIn = keyframes`
  0% {
    opacity: 0;
    visibility: hidden;
  }
  100% {
    opacity: 1;
    visibility: visible;
  }`

const dynopen = ({ $open }) =>
  $open
    ? css`
        animation-direction: normal;
        opacity: 1;
        visibility: visible;
      `
    : css`
        animation-direction: reverse;
        opacity: 0;
        visibility: hidden;
      `

const dynanimate = ({ $animate }) => {
  return !$animate
    ? css`
        animation-name: none !important;
      `
    : ''
}

const ActionMenuWrapper = styled.div`
  label: action-menu-wrapper;
  display: block;

  z-index: 1000;

  position: absolute;

  opacity: 0;
  visibility: hidden;

  right: 64px;

  &:not(:empty)::after {
    right: -9px;
    border-width: 9px 0 9px 9px;
    border-color: transparent transparent transparent #2c2c2c;

    content: '';

    display: block;
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
  }

  animation-duration: 0.2s;
  animation-timing-function: ease-out;

  &:active {
  }

  &:focus {
  }

  ${dynopen}
  ${dynanimate}

  ${({ $direction, $top, $bottom }) => {
    return $direction === 'upward'
      ? css`
          bottom: ${$bottom}px;
          top: auto;
          animation-name: ${fadeIn}, ${animUpward};

          &::after {
            bottom: 18px;
            top: auto;
          }
        `
      : css`
          top: ${$top}px;
          bottom: auto;

          animation-name: ${fadeIn}, ${animDownward};

          &::after {
            top: 18px;
            bottom: auto;
          }
        `
  }}
`

export const DataTableActionMenu = React.forwardRef(({ actions }, ref) => {
  const menuRef = useRef(null)

  const [{ items, open, top, bottom, targetRect }, setActionMenu] = useState({
    index: undefined,
    open: false,
    items: [],
    top: 0,
    bottom: 0,
    targetRect: { top: 0, bottom: 0 }
  })

  const [animate, setAnimate] = useState(true)

  useImperativeHandle(ref, () => ({
    showMenu: (rowIndex, { top, bottom, targetRect }) => {
      if (actions[rowIndex]) {
        setAnimate(true)

        setActionMenu(({ index, ...prev }) => ({
          index: rowIndex,
          open: index === rowIndex ? !prev.open : true,
          items: [...actions[rowIndex].items],
          top,
          bottom,
          targetRect
        }))
      }
    }
  }))

  const [viewport] = useViewport({
    scroll: (e, viewport) => {
      windowHeight.current = viewport.windowHeight
    },
    resize: (e, viewport) => {
      windowHeight.current = viewport.windowHeight
    }
  })
  const windowHeight = useRef(viewport.windowHeight)

  const onClickOutside = e => {
    setAnimate(true)

    setActionMenu(prev => ({
      ...prev,
      open: false
    }))
  }

  const handleClickOutside = event => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      if (
        event.target.dataset &&
        event.target.dataset.muTest &&
        event.target.dataset.muTest.includes('dt-actionmenu-trigger')
      ) {
        // do nothing
      } else {
        onClickOutside(event)
      }
    }
  }

  const handleAnimationEnd = () => {
    setAnimate(false)
  }

  useEffect(() => {
    if (menuRef.current) {
      if (open) {
        document.addEventListener('mousedown', handleClickOutside, {
          capture: true
        })
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside, {
          capture: true
        })
      }
    }
  }, [open])

  const elMenuBottom = rectBottom(targetRect) + 32 * items.length

  const _direction =
    elMenuBottom + 20 >= windowHeight.current ? 'upward' : 'downward'

  return (
    <ActionMenuWrapper
      $open={open}
      $top={top}
      $bottom={bottom}
      $direction={_direction}
      ref={menuRef}
      tabIndex={0}
      $animate={animate}
      onAnimationEnd={handleAnimationEnd}
    >
      {items.length > 0 && (
        <ContextMenu
          variant={CONTEXT_MENU_VARIANT_DARK}
          items={items}
          itemsClickCallback={handleClickOutside}
        />
      )}
    </ActionMenuWrapper>
  )
})

DataTableActionMenu.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      items: PropTypes.array,
      className: PropTypes.string,
      disabled: PropTypes.bool,
      'data-mu-test': PropTypes.string
    })
  )
}
